import { useState } from 'react';
import { api } from '../../lib/api';
import type { CockpitParty } from '../../types';
import { Button, Dialog } from '../ui';
import { deadlineDate } from '../../utils/deadlineDates';

interface Props {
  dealId: string;
  party: CockpitParty;
  nextLabel: string | null;
  nextDate: string | null;
  channel?: 'sms' | 'email';
  initialMessage?: string;
  initialSubject?: string;
  onClose: () => void;
}

export default function NudgeComposer({ dealId, party, nextLabel, nextDate, channel: initialChannel, initialMessage, initialSubject, onClose }: Props) {
  const firstName = (party.name || '').split(' ')[0] || 'there';
  const dateStr = deadlineDate(nextDate) ?? '';
  const [channel, setChannel] = useState<'sms' | 'email'>(initialChannel ?? (party.phone ? 'sms' : 'email'));
  const [message, setMessage] = useState(
    initialMessage ?? (nextLabel ? `Hi ${firstName}, checking in on ${nextLabel}${dateStr ? ` (due ${dateStr})` : ''}. Thanks, Joseph`
      : `Hi ${firstName}, checking in on this deal. Thanks, Joseph`));
  const [subject, setSubject] = useState(initialSubject || 'Quick check-in on the escrow');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [drafting, setDrafting] = useState(false);

  const draftWithAI = async () => {
    if (!party.contact_id) { setErr('This party is not linked to a contact.'); return; }
    setDrafting(true); setErr(null);
    try {
      const r = await api.draftPartyMessage(dealId, party.contact_id, channel);
      if (r.message) setMessage(r.message);
      if (channel === 'email' && r.subject) setSubject(r.subject);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Draft failed'); }
    finally { setDrafting(false); }
  };

  const send = async () => {
    if (!party.contact_id) { setErr('This party is not linked to a GHL contact.'); return; }
    setBusy(true); setErr(null);
    try {
      await api.nudgeParty(dealId, { contact_id: party.contact_id, channel, message, subject: channel === 'email' ? subject : undefined });
      setSent(true);
      setTimeout(onClose, 900);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Send failed'); }
    finally { setBusy(false); }
  };

  return (
    /* OO5d: through the Dialog primitive. The footer keeps its SPLIT layout - "Draft with AI"
       sits left, away from the destructive-adjacent Send, which is why it is not a plain row. */
    <Dialog
      open
      onClose={onClose}
      title={`Nudge ${party.name || 'party'}`}
      size="md"
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          {!sent
            ? <Button variant="outline" size="sm" onClick={draftWithAI} disabled={drafting || !party.contact_id} title="Let AI draft this from the deal's context">{drafting ? 'Drafting…' : 'Draft with AI'}</Button>
            : <span />}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            {!sent && <Button size="sm" onClick={send} disabled={busy || !message.trim()}>Send</Button>}
          </div>
        </div>
      }
    >
        <div className="space-y-3">
          {err && <div className="text-sm text-red-600">{err}</div>}
          {sent ? <div className="text-sm text-success">Sent.</div> : (
            <>
              <div className="flex gap-2">
                <button onClick={() => setChannel('sms')} disabled={!party.phone} className={`px-3 py-1 rounded text-sm border ${channel === 'sms' ? 'bg-primary-500 text-white border-primary-500' : 'text-gray-600'} disabled:opacity-40`}>SMS</button>
                <button onClick={() => setChannel('email')} disabled={!party.email} className={`px-3 py-1 rounded text-sm border ${channel === 'email' ? 'bg-primary-500 text-white border-primary-500' : 'text-gray-600'} disabled:opacity-40`}>Email</button>
              </div>
              {channel === 'email' && <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className="w-full border rounded px-2 py-1 text-sm" />}
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} className="w-full border rounded px-2 py-1 text-sm" />
            </>
          )}
        </div>
    </Dialog>
  );
}
